import { useMemo, useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import {
  Alert,
  Button,
  DataTable,
  Icon,
  InputText,
  ListScreen,
  RigoFilters,
  SectionCard,
  Tabs,
  type ColumnDef,
  PageWrapper,
} from "@/deps/ui-library";
import { useEmployeeMovementDrafts, useEmployees, usePartyStatement } from "@/api/queries";
import type { EmployeeLedgerRow, EmployeeMovement, PartyStatement } from "@/api/types";
import { useCan } from "@/app/session";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { KpiStrip, type Kpi } from "@/components/KpiStrip";
import { PageHeader } from "@/components/PageHeader";
import { PartyDialog } from "@/features/parties/PartiesPage";
import { TYPE_LABELS } from "@/features/parties/PartyLedger";
import { formatDate, today } from "@/lib/dates";
import { formatAmount } from "@/lib/money";
import { EmployeeMovementDialog } from "./EmployeeMovementDialog";
import { DateRangeField } from "@/components/DateField";

const NUMERIC = { textAlign: "right" as const, numeric: true };

type Segment = "all" | "owed" | "recoverable";

const standing = (balance: string) => {
  const figure = Number(balance);
  if (figure > 0) return "Owed to employee";
  if (figure < 0) return "Recoverable";
  return "Settled";
};

/** Payments that aren't on the ledger yet, because the payment rule wants them approved first. */
function WaitingToPost({ partyId }: { partyId?: string }) {
  const { data } = useEmployeeMovementDrafts();
  const rows = (data ?? []).filter((draft) => !partyId || draft.partyId === partyId);
  const columns = useMemo<ColumnDef<EmployeeMovement>[]>(
    () => [
      {
        header: "Employee",
        id: "name",
        cell: ({ row }) => (
          <Link
            to="/accounting/employees/movements/$id"
            params={{ id: row.original.id }}
            className="text-primary underline"
          >
            {row.original.partyName}
          </Link>
        ),
      },
      { header: "Date", id: "date", cell: ({ row }) => formatDate(row.original.date) },
      { header: "Details", id: "note", cell: ({ row }) => row.original.note ?? "" },
      {
        header: "Amount",
        id: "amount",
        meta: NUMERIC,
        cell: ({ row }) => formatAmount(row.original.amount),
      },
    ],
    [],
  );

  if (rows.length === 0) return null;
  return (
    <SectionCard title="Waiting to be posted" flush>
      <DataTable
        aria-label="Waiting to be posted"
        size="sm"
        enableSorting={false}
        columns={columns}
        data={rows}
      />
    </SectionCard>
  );
}

/** Everyone on the employee ledger, and what's owed each way (ADR-0060). */
export function EmployeesPage() {
  const can = useCan();
  const { data, isPending, error, refetch } = useEmployees();
  const [search, setSearch] = useState("");
  const [segment, setSegment] = useState<Segment>("all");
  const [moving, setMoving] = useState<"payment" | "receipt" | null>(null);
  const [adding, setAdding] = useState(false);

  const rows = data ?? [];
  const owed = rows.filter((r) => Number(r.balance) > 0);
  const recoverable = rows.filter((r) => Number(r.balance) < 0);
  const sum = (list: EmployeeLedgerRow[]) =>
    list.reduce((total, r) => total + Number(r.balance), 0);

  const kpis: Kpi[] = [
    {
      label: "Owed to employees",
      value: formatAmount(sum(owed)),
      detail: `${owed.length} with a credit balance`,
    },
    {
      label: "Recoverable from employees",
      value: formatAmount(Math.abs(sum(recoverable))),
      detail: `${recoverable.length} with a debit balance`,
    },
    { label: "Net balance", value: formatAmount(sum(rows)), detail: "Across all employee ledgers" },
  ];

  const text = search.trim().toLowerCase();
  const shown = rows
    .filter((r) =>
      segment === "all" ? true : segment === "owed" ? Number(r.balance) > 0 : Number(r.balance) < 0,
    )
    .filter((r) => !text || `${r.name} ${r.taxId ?? ""}`.toLowerCase().includes(text));

  const columns = useMemo<ColumnDef<EmployeeLedgerRow>[]>(
    () => [
      {
        header: "Employee",
        id: "name",
        cell: ({ row }) => (
          <Link
            to="/accounting/employees/$id"
            params={{ id: row.original.id }}
            className="text-primary underline"
          >
            {row.original.name}
          </Link>
        ),
      },
      { header: "PAN", id: "pan", cell: ({ row }) => row.original.taxId ?? "–" },
      {
        header: "Last movement",
        id: "last",
        cell: ({ row }) =>
          row.original.lastMovement ? formatDate(row.original.lastMovement) : "–",
      },
      {
        header: "Balance",
        id: "balance",
        meta: NUMERIC,
        cell: ({ row }) => formatAmount(row.original.balance),
      },
      { header: "", id: "standing", cell: ({ row }) => standing(row.original.balance) },
    ],
    [],
  );

  const segments: Array<{ value: Segment; label: string; count: number }> = [
    { value: "all", label: "All", count: rows.length },
    { value: "owed", label: "Owed to employee", count: owed.length },
    { value: "recoverable", label: "Recoverable", count: recoverable.length },
  ];

  return (
    // The shell's main has the padding.
    <ListScreen.Root className="p-0">
      <ListScreen.Header
        title="Employee ledgers"
        description="Payments, receipts and journals, kept apart from suppliers."
        actions={
          can("accounting.employees.record") ? (
            <>
              <Button variant="secondary" size="sm" leftIcon="Plus" onClick={() => setAdding(true)}>
                New employee
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setMoving("receipt")}>
                Receive from employee
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setMoving("payment")}>
                Pay employee
              </Button>
            </>
          ) : undefined
        }
      />

      <KpiStrip items={kpis} loading={isPending} />

      <WaitingToPost />

      <Tabs.Root value={segment} onValueChange={(details) => setSegment(details.value as Segment)}>
        <Tabs.List aria-label="Show">
          {segments.map((one) => (
            <Tabs.Trigger key={one.value} value={one.value}>
              {one.label}
              <span className="text-muted-foreground ml-1.5">{one.count}</span>
            </Tabs.Trigger>
          ))}
          <Tabs.Indicator />
        </Tabs.List>
        <ListScreen.TabPanel value={segment}>
          <ListScreen.Filters>
            <RigoFilters.Root>
              <RigoFilters.StaticRow>
                <div className="w-60">
                  <InputText.FilterSearch
                    aria-label="Search employees"
                    placeholder="Search name or PAN"
                    value={search}
                    onChange={(event) => setSearch(event.target.value)}
                    onClear={() => setSearch("")}
                  />
                </div>
              </RigoFilters.StaticRow>
            </RigoFilters.Root>
          </ListScreen.Filters>
          <ListScreen.Body
            loading={isPending}
            error={error}
            onRetry={() => void refetch()}
            empty={shown.length === 0}
            // With employees on the ledger, an empty table is this tab or this search.
            filtered={rows.length > 0}
            onClearFilters={() => {
              setSearch("");
              setSegment("all");
            }}
            emptyState={{
              title: "No employees yet",
              description: "Add the people you pay. Their balances stay apart from suppliers.",
              icon: <Icon name="Users" size="lg" color="inherit" />,
            }}
            filteredEmptyState={{
              title: "No employee matches",
              description: "Try another name or PAN, or show all employees.",
            }}
          >
            <DataTable
              aria-label="Employees"
              size="sm"
              enableSorting={false}
              columns={columns}
              data={shown}
            />
          </ListScreen.Body>
        </ListScreen.TabPanel>
      </Tabs.Root>

      {moving ? <EmployeeMovementDialog kind={moving} onClose={() => setMoving(null)} /> : null}
      {adding ? <PartyDialog kind="employee" onClose={() => setAdding(false)} /> : null}
    </ListScreen.Root>
  );
}

type Line = PartyStatement["lines"][number];

/** One employee's ledger, across both control accounts. */
export function EmployeeDetailPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const can = useCan();
  const { data: employees } = useEmployees();
  const employee = employees?.find((e) => e.id === id);
  const [from, setFrom] = useState(`${today().slice(0, 4)}-01-01`);
  const [to, setTo] = useState(today());
  const [moving, setMoving] = useState<"payment" | "receipt" | null>(null);
  const { data: statement, error } = usePartyStatement(id, "employee", from, to);

  const columns = useMemo<ColumnDef<Line>[]>(
    () => [
      { header: "Date", id: "date", cell: ({ row }) => formatDate(row.original.date) },
      { header: "Document", accessorKey: "number" },
      {
        header: "Type",
        id: "type",
        cell: ({ row }) => TYPE_LABELS[row.original.documentType] ?? row.original.documentType,
      },
      { header: "Details", id: "details", cell: ({ row }) => row.original.details ?? "" },
      {
        header: "Debit",
        id: "debit",
        meta: NUMERIC,
        cell: ({ row }) => (Number(row.original.debit) ? formatAmount(row.original.debit) : "–"),
      },
      {
        header: "Credit",
        id: "credit",
        meta: NUMERIC,
        cell: ({ row }) => (Number(row.original.credit) ? formatAmount(row.original.credit) : "–"),
      },
      {
        header: "Balance",
        id: "balance",
        meta: NUMERIC,
        cell: ({ row }) => formatAmount(row.original.balance),
      },
    ],
    [],
  );

  const kpis: Kpi[] = employee
    ? [
        {
          label: "Balance",
          value: formatAmount(employee.balance),
          detail: standing(employee.balance),
        },
        {
          label: "Paid to employee",
          value: formatAmount(statement?.totalDebit ?? 0),
          detail: "In this period",
        },
        {
          label: "Charged and received",
          value: formatAmount(statement?.totalCredit ?? 0),
          detail: "Journals and receipts",
        },
      ]
    : [];

  return (
    <div className="flex flex-col gap-4">
      <Breadcrumbs
        items={[
          { label: "Employee ledgers", to: "/accounting/employees" },
          { label: employee?.name ?? "" },
        ]}
      />
      <PageHeader
        title={employee?.name ?? "Employee"}
        subtitle={employee?.taxId ? `PAN ${employee.taxId}` : undefined}
        actions={
          can("accounting.employees.record") ? (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" size="sm" onClick={() => setMoving("receipt")}>
                Receive from employee
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setMoving("payment")}>
                Pay employee
              </Button>
            </div>
          ) : undefined
        }
      />
      {error ? (
        <Alert
          config={{ variant: "subtle", colorScheme: "danger" }}
          description={(error as Error).message}
        />
      ) : null}
      <KpiStrip items={kpis} loading={!employee} />
      <WaitingToPost partyId={id} />
      <div className="flex flex-wrap gap-3">
        <DateRangeField
          label="Period"
          variant="input"
          value={{ from, to }}
          onChange={(range) => {
            setFrom(range.from);
            setTo(range.to);
          }}
        />
      </div>
      <SectionCard
        title="Ledger"
        note={
          statement
            ? `Opening ${formatAmount(statement.opening)} · Closing ${formatAmount(statement.closing)}`
            : undefined
        }
        flush
      >
        {statement && statement.lines.length === 0 ? (
          <p className="text-fg-muted p-4 text-sm">Nothing moved in this period.</p>
        ) : (
          <DataTable
            aria-label="Employee ledger"
            size="sm"
            enableSorting={false}
            columns={columns}
            data={statement?.lines ?? []}
          />
        )}
      </SectionCard>
      {moving ? (
        <EmployeeMovementDialog kind={moving} partyId={id} onClose={() => setMoving(null)} />
      ) : null}
    </div>
  );
}
